import React, { useState, useEffect } from 'react';
import { Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../mock';
import VideoThumbnail from './VideoThumbnail';

const Gallery = () => {
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (selectedVideoIndex !== null) {
        setSelectedVideoIndex(null);
      }
    };

    if (selectedVideoIndex !== null) {
      window.addEventListener('scroll', handleScroll);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.style.overflow = 'unset';
    };
  }, [selectedVideoIndex]);

  const handleVideoClick = (index) => {
    if (isMobile && galleryImages[index].type === 'video') {
      setSelectedVideoIndex(index);
    }
  };

  const handlePrevious = () => {
    if (selectedVideoIndex > 0) {
      setSelectedVideoIndex(selectedVideoIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedVideoIndex < galleryImages.length - 1) {
      setSelectedVideoIndex(selectedVideoIndex + 1);
    }
  };

  return (
    <section id="gallery" className="pad-120 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-4 px-4 py-2 bg-purple-500/10 rounded-full">
            <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
              <Camera size={16} />
              Our Work
            </span>
          </div>
          <h2 className="heading-1 mb-6 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Results That Speak
          </h2>
          <p className="body-large text-gray-400 max-w-3xl mx-auto">
            See the transformation. Real vehicles, real results from our premium detailing services.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {galleryImages.map((item, index) => (
            <div 
              key={item.id}
              onClick={() => handleVideoClick(index)}
              className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-gray-900 border border-gray-800 hover:border-purple-500/50 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
            >
              {item.type === 'video' ? (
                <VideoThumbnail
                  videoUrl={item.url}
                  title={item.title}
                  description={item.description}
                  className="w-full h-full"
                />
              ) : (
                <img 
                  src={item.url} 
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              )}
            </div>
          ))}
        </div>

        {/* Mobile Video Viewer */}
        {selectedVideoIndex !== null && isMobile && (
          <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setSelectedVideoIndex(null)}
              className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <X size={24} className="text-white" />
            </button>

            {/* Previous button */}
            {selectedVideoIndex > 0 && (
              <button
                onClick={handlePrevious}
                className="absolute left-4 z-50 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronLeft size={28} className="text-white" />
              </button>
            )}

            {/* Next button */}
            {selectedVideoIndex < galleryImages.length - 1 && (
              <button
                onClick={handleNext}
                className="absolute right-4 z-50 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronRight size={28} className="text-white" />
              </button>
            )}

            {/* Video container - 85% of screen */}
            <div className="w-[85vw] h-[85vh] flex items-center justify-center">
              {galleryImages[selectedVideoIndex].type === 'video' ? (
                <video
                  src={galleryImages[selectedVideoIndex].url}
                  className="w-full h-full object-contain"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                />
              ) : (
                <img
                  src={galleryImages[selectedVideoIndex].url}
                  alt={galleryImages[selectedVideoIndex].title}
                  className="w-full h-full object-contain"
                />
              )}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-4 px-8 py-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-sm border border-purple-500/20 rounded-full">
            <div className="flex items-center gap-2">
              <Camera size={20} className="text-purple-400" />
              <span className="text-white font-semibold">Want results like these?</span>
            </div>
            <button 
              onClick={() => {
                const element = document.querySelector('#booking-walkthrough');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-6 py-2 rounded-full transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;